import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchUserProjects, refreshUserProjects } from '../redux/actions/userProjectsAction';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';

/**
 * Custom hook to fetch and manage user projects
 * @param page Current page number for pagination
 * @param limit Number of items per page
 * @returns Object containing user projects data and related functions
 */
export const useUserProjects = (page = 1, limit = 10) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector(state => state.auth);
  const {
    userProjects,
    pagination,
    loading,
    error,
    lastLoadTime,
    // Filtered user projects
    filteredUserProjects,
    // Dashboard data
    userProjectCount,
    userPaymentReceived,
    userBilledAmount,
    userPipelineAmount,
    userStatusCounts,
  } = useAppSelector(state => state.userProjects);

  // Log current state for debugging
  console.log('useUserProjects - User ID:', user?.id);
  console.log('useUserProjects - Current userProjects state:', {
    projectsCount: userProjects?.length || 0,
    loading,
    error,
    lastLoadTime,
    dashboardData: {
      userProjectCount,
      userPaymentReceived,
      userBilledAmount,
      userPipelineAmount,
    },
  });

  // Handle authentication errors
  useEffect(() => {
    if (
      error &&
      (error.includes('Authentication') ||
        error.includes('Unauthorized') ||
        error.includes('token'))
    ) {
      console.error('useUserProjects - Authentication error:', error);

      // Check if we're already on the login page to avoid redirect loops
      const isLoginPage = location.pathname.includes('/auth/login');

      if (!isLoginPage) {
        toast.error('Authentication error. Please log in again.');
        console.log('useUserProjects - Redirecting to login page due to authentication error');

        // Redirect to login page after a short delay
        const timer = setTimeout(() => {
          navigate('/auth/login');
        }, 1500);

        return () => clearTimeout(timer);
      }
    }
  }, [error, navigate, location.pathname]);

  // Fetch user projects on mount ONLY (not when page/limit changes)
  useEffect(() => {
    if (user?.id && !userProjects) {
      // Only fetch if we don't have data yet
      console.log(
        `useUserProjects - Initial fetch for user ${user.id}, page ${page}, limit ${limit}`
      );

      // Dispatch the fetchUserProjects action
      dispatch(
        fetchUserProjects({
          userId: user.id,
          page: 1, // Always start with page 1 for initial load
          limit: 50, // Get more data initially to reduce subsequent calls
        })
      )
        .then(result => {
          console.log('useUserProjects - Initial fetch result:', result);
        })
        .catch(err => {
          console.error('useUserProjects - Initial fetch error:', err);
        });
    } else {
      console.log(
        'useUserProjects - Skipping fetch: user ID =',
        user?.id,
        ', has data =',
        !!userProjects
      );
    }
  }, [dispatch, user?.id]); // Remove page and limit from dependencies

  // Function to refresh user projects
  const refreshProjects = () => {
    if (user?.id) {
      console.log(`useUserProjects - Refreshing projects for user ${user.id}`);
      return dispatch(
        refreshUserProjects({
          userId: user.id,
          page,
          limit,
        })
      );
    }
    console.log('useUserProjects - No user ID available, skipping refresh');
    return Promise.reject('User not logged in');
  };

  return {
    userProjects,
    filteredUserProjects, // Add filteredUserProjects to the return object
    pagination,
    isLoading: loading,
    error,
    refreshProjects,
    // Return dashboard data
    dashboardData: {
      userProjectCount,
      userPaymentReceived,
      userBilledAmount,
      userPipelineAmount,
      userStatusCounts,
    },
  };
};
